import type {
  CheckboxProperties,
  DropdownProperties,
  FormElement,
  RadioGroupProperties,
} from '../model/form.types';
import type { ConditionalFormRule } from '../model/form-rules.store';

export interface RuleTriggerOption {
  value: string;
  label: string;
}

export type RuleOptionUsage = Pick<ConditionalFormRule, 'id' | 'triggerFieldId' | 'triggerOption'>;

export const ACTIVE_RULE_CREATION_DRAFT_USAGE_ID = '__active-rule-draft__';

export function getPendingRuleCreationDraftUsageId(index: number) {
  return `__pending-rule-draft-${index}__`;
}

export function hasRuleOptionUsageValue(value: string | null | undefined) {
  return String(value ?? '').trim().length > 0;
}

export function appendRuleOptionUsage(
  usages: RuleOptionUsage[],
  id: string,
  triggerFieldId: string | null | undefined,
  triggerOption: string | null | undefined
) {
  if (!triggerFieldId || !hasRuleOptionUsageValue(triggerOption)) return;
  const normalizedTriggerOption = String(triggerOption).trim();

  usages.push({
    id,
    triggerFieldId,
    triggerOption: normalizedTriggerOption,
  });
}

function getDropdownAlternatives(element: FormElement) {
  return ((element.properties as DropdownProperties | undefined)?.alternatives ?? []).map(
    (option) => ({
      value: option.value,
      label: option.value,
    })
  );
}

function getCheckboxOptions(element: FormElement) {
  return ((element.properties as CheckboxProperties | undefined)?.options ?? []).map((option) => ({
    value: option.text,
    label: option.text,
  }));
}

function getRadioGroupOptions(element: FormElement) {
  return ((element.properties as RadioGroupProperties | undefined)?.options ?? []).map(
    (option) => ({
      value: option,
      label: option,
    })
  );
}

export function getRuleTriggerOptions(
  element: FormElement | null | undefined
): RuleTriggerOption[] {
  if (!element) return [];

  if (element.type === 'DROPDOWN') {
    return getDropdownAlternatives(element);
  }

  if (element.type === 'CHECKBOX') {
    return getCheckboxOptions(element);
  }

  if (element.type === 'RADIO_GROUP') {
    return getRadioGroupOptions(element);
  }

  return [];
}

export function getUsedTriggerOptions(
  rules: readonly RuleOptionUsage[],
  triggerFieldId: string,
  ignoredRuleId?: string
) {
  return new Set(
    rules
      .filter((rule) => rule.triggerFieldId === triggerFieldId && rule.id !== ignoredRuleId)
      .map((rule) => String(rule.triggerOption).trim())
  );
}

export function getAvailableRuleTriggerOptions(
  element: FormElement | null | undefined,
  rules: readonly RuleOptionUsage[],
  ignoredRuleId?: string
) {
  if (!element) return [];

  const usedOptions = getUsedTriggerOptions(rules, element.id, ignoredRuleId);

  return getRuleTriggerOptions(element).filter(
    (option) => !usedOptions.has(String(option.value).trim())
  );
}

export function hasAvailableRuleTriggerOption(
  element: FormElement | null | undefined,
  rules: readonly RuleOptionUsage[]
) {
  if (!element) return false;

  return getAvailableRuleTriggerOptions(element, rules).some((option) => option.value.trim());
}

export function countRuleTriggerOptions(element: FormElement | null | undefined) {
  const values = getRuleTriggerOptions(element)
    .map((option) => String(option.value).trim())
    .filter(Boolean);

  return new Set(values).size;
}

export interface RuleLimitEntry {
  triggerFieldId: string;
  triggerOption: string;
  isDraft: boolean;
}

/**
 * A saved rule whose option was renamed or removed holds no slot: it can never fire, and counting
 * it would hide "Adicionar Regra" while an option is still free. A draft always holds its slot,
 * chosen option or not.
 */
export function countTriggerFieldRules(entries: readonly RuleLimitEntry[], element: FormElement) {
  const options = new Set(
    getRuleTriggerOptions(element).map((option) => String(option.value).trim())
  );

  return entries.filter(
    (entry) =>
      entry.triggerFieldId === element.id &&
      (entry.isDraft || options.has(String(entry.triggerOption).trim()))
  ).length;
}

/**
 * The option usages keep the floor of never offering a rule whose "Quando selecionar" list would
 * be empty.
 */
export function canAddRuleForTriggerElement(
  element: FormElement | null | undefined,
  entries: readonly RuleLimitEntry[],
  usages: readonly RuleOptionUsage[]
) {
  if (!element) return false;

  return (
    countTriggerFieldRules(entries, element) < countRuleTriggerOptions(element) &&
    hasAvailableRuleTriggerOption(element, usages)
  );
}
